import React, { useState } from 'react';
import { 
  Building2, Home, Car, Bike, Truck, Smartphone, Armchair, Shirt, Baby, Gem, Utensils, BookOpen, Wrench,
  Briefcase, HeartHandshake, LayoutGrid, Settings2, Map, BedDouble, Building, Castle, Tractor,
  Store, Palette, TreePine, Sparkles
} from 'lucide-react';
import { 
  SiToyota, 
  SiBmw, 
  SiAudi, 
  SiHyundai, 
  SiKia, 
  SiNissan, 
  SiFord, 
  SiChevrolet, 
  SiHonda, 
  SiMitsubishi, 
  SiVolkswagen, 
  SiJeep, 
  SiMg 
} from 'react-icons/si';
import { CategoryNode } from '../types';
import { BRAND_LOGO_PUBLIC_PATH } from '../config/branding';

/**
 * Brands map either to a Simple Icons glyph or to a vendored emblem under
 * `public/brands/`, generated by `npm run brand:logos`.
 */
const BRAND_ICONS: Record<string, any> = {
  'toyota': SiToyota,
  'تويوتا': SiToyota,
  'mercedes': '/brands/mercedes.png',
  'mercedesbenz': '/brands/mercedes.png',
  'مرسيدس': '/brands/mercedes.png',
  'bmw': SiBmw,
  'بي ام دبليو': SiBmw,
  'بي إم دبليو': SiBmw,
  'audi': SiAudi,
  'أودي': SiAudi,
  'اودي': SiAudi,
  'hyundai': SiHyundai,
  'هونداي': SiHyundai,
  'هيونداي': SiHyundai,
  'kia': SiKia,
  'كيا': SiKia,
  'nissan': SiNissan,
  'نيسان': SiNissan,
  'ford': SiFord,
  'فورد': SiFord,
  'chevrolet': SiChevrolet,
  'شيفروليه': SiChevrolet,
  'شفروليه': SiChevrolet,
  'lexus': '/brands/lexus.png',
  'لكزس': '/brands/lexus.png',
  'honda': SiHonda,
  'هوندا': SiHonda,
  'mitsubishi': SiMitsubishi,
  'ميتسوبيشي': SiMitsubishi,
  'volkswagen': SiVolkswagen,
  'فولكس واجن': SiVolkswagen,
  'jeep': SiJeep,
  'جيب': SiJeep,
  'mg': SiMg,
  'ام جي': SiMg,
  'chery': '/brands/chery.png',
  'شيري': '/brands/chery.png',
  'geely': '/brands/geely.png',
  'جيلي': '/brands/geely.png'
};

/**
 * Official brand colors (hex values published by Simple Icons). `dark` is a
 * lightened variant used on dark backgrounds and on the active blue chip, since
 * marques like Kia and Jeep are near-black and would otherwise disappear.
 */
const BRAND_COLORS: Record<string, { light: string; dark: string }> = {
  'toyota': { light: '#EB0A1E', dark: '#FF4757' },
  'bmw': { light: '#0066B1', dark: '#4FA3E3' },
  'audi': { light: '#BB0A30', dark: '#EE4467' },
  'hyundai': { light: '#002C5E', dark: '#5C9BE0' },
  'kia': { light: '#05141F', dark: '#E8EDF2' },
  'nissan': { light: '#C3002F', dark: '#F2536E' },
  'ford': { light: '#00274E', dark: '#5A9BE0' },
  'chevrolet': { light: '#CD9834', dark: '#E5B75E' },
  'honda': { light: '#E40521', dark: '#FF4C60' },
  'mitsubishi': { light: '#E60012', dark: '#FF4652' },
  'volkswagen': { light: '#151F5D', dark: '#8E9BEC' },
  'jeep': { light: '#111111', dark: '#F1F5F9' },
  'mg': { light: '#FF0000', dark: '#FF5C5C' }
};

/** Keys are matched exactly, so `mg` can no longer hijack any slug containing "mg". */
const normalizeBrandKey = (value: string) => value.toLowerCase().replace(/[\s_\-.]+/g, '');

const BRAND_ICON_INDEX: Record<string, any> = Object.keys(BRAND_ICONS).reduce((acc, key) => {
  acc[normalizeBrandKey(key)] = BRAND_ICONS[key];
  return acc;
}, {} as Record<string, any>);

function resolveBrandColor(iconValue: any) {
  const latinKey = Object.keys(BRAND_COLORS).find((key) => BRAND_ICONS[key] === iconValue);
  return latinKey ? BRAND_COLORS[latinKey] : undefined;
}

const CATEGORY_ICONS: Record<string, React.FC<any>> = {
  'real_estate': Building2,
  'real_estate_projects': Building2,
  'new_projects': Home,
  'vehicles': Car,
  'cars': Car,
  'motorcycles': Bike,
  'heavy_equipment': Truck,
  'car_parts': Settings2,
  'engine_parts': Settings2,
  'electronics': Smartphone,
  'furniture': Armchair,
  'living_room': Armchair,
  'fashion': Shirt,
  'fashion_men': Shirt,
  'fashion_women': Shirt,
  'mother_baby': Baby,
  'luxury': Gem,
  'food': Utensils,
  'books': BookOpen,
  'tools': Wrench,
  'services': Briefcase,
  'jobs': Briefcase,
  'services_jobs': Briefcase,
  'donations': HeartHandshake,
  'cleaning': Sparkles,
  'lands': Map,
  'اراضي': Map,
  'أراضي': Map,
  'bedrooms': BedDouble,
  'bedroom': BedDouble,
  'beds': BedDouble,
  'غرف نوم': BedDouble,
  'غرفة نوم': BedDouble,
  'apartments': Building,
  'شقق': Building,
  'شقة': Building,
  'buildings': Building2,
  'عمارات': Building2,
  'villas_palaces': Castle,
  'villas': Castle,
  'فلل': Castle,
  'فلة': Castle,
  'فيلا': Castle,
  'فلل وقصور': Castle,
  'farms': Tractor,
  'مزارع': Tractor,
  'مزرعة': Tractor,
  'commercial_shops': Store,
  'commercial': Store,
  'محلات تجارية': Store,
  'محل تجاري': Store,
  'محلات ومكاتب': Store,
  'offices': Briefcase,
  'مكاتب': Briefcase,
  'مكتب': Briefcase,
  'chalets': Home,
  'شاليهات': Home,
  'شاليه': Home,
  'kitchen': Utensils,
  'kitchens': Utensils,
  'مطبخ': Utensils,
  'مطابخ': Utensils,
  'dining_room': Utensils,
  'غرف طعام': Utensils,
  'غرفة طعام': Utensils,
  'living-rooms': Armchair,
  'غرف معيشة': Armchair,
  'غرفة معيشة': Armchair,
  'مجالس ومفروشات': Armchair,
  'decor': Palette,
  'ديكور ولوحات': Palette,
  'office-furniture': Briefcase,
  'أثاث مكتبي': Briefcase,
  'outdoor-furniture': TreePine,
  'أثاث خارجي': TreePine,
};
const CATEGORY_COLORS: Record<string, string> = {
  'real_estate': 'text-emerald-500',
  'real_estate_projects': 'text-emerald-600',
  'new_projects': 'text-emerald-500',
  'vehicles': 'text-blue-500',
  'cars': 'text-blue-600',
  'motorcycles': 'text-blue-400',
  'heavy_equipment': 'text-slate-600',
  'car_parts': 'text-slate-500',
  'engine_parts': 'text-slate-600',
  'electronics': 'text-indigo-500',
  'furniture': 'text-amber-600',
  'living_room': 'text-amber-500',
  'fashion': 'text-pink-500',
  'fashion_men': 'text-sky-500',
  'fashion_women': 'text-pink-400',
  'mother_baby': 'text-rose-400',
  'luxury': 'text-purple-500',
  'food': 'text-orange-500',
  'books': 'text-teal-500',
  'tools': 'text-zinc-600',
  'services': 'text-cyan-500',
  'jobs': 'text-cyan-600',
  'services_jobs': 'text-cyan-500',
  'donations': 'text-red-500',
  'miscellaneous': 'text-violet-500',
  'other_goods': 'text-violet-400',
  'cleaning': 'text-sky-400',
  'lands': 'text-amber-700',
  'bedrooms': 'text-indigo-400',
  'bedroom': 'text-indigo-400',
  'beds': 'text-indigo-400',
  'apartments': 'text-blue-500',
  'villas_palaces': 'text-purple-600',
  'villas': 'text-purple-600',
  'farms': 'text-green-600',
  'commercial_shops': 'text-sky-500',
  'commercial': 'text-sky-500',
  'offices': 'text-slate-600',
  'chalets': 'text-teal-500',
  'kitchen': 'text-orange-400',
  'kitchens': 'text-orange-400',
  'dining_room': 'text-orange-500',

  'buildings': 'text-slate-500',
  'decor': 'text-pink-500',
  'office-furniture': 'text-slate-700',
  'outdoor-furniture': 'text-green-700',
};

interface DynamicCategoryIconProps {
  category: CategoryNode;
  className?: string;
  isActive?: boolean;
}

interface BrandIconProps {
  icon: any;
  name: string;
  className: string;
  isActive: boolean;
}

const BrandIcon: React.FC<BrandIconProps> = ({ icon, name, className, isActive }) => {
  // Real multi-color logos keep their own palette. A white pill is added on dark
  // backgrounds and on the active chip so dark marques stay readable.
  if (typeof icon === 'string') {
    return (
      <span
        className={`${className} flex items-center justify-center overflow-hidden ${
          isActive
            ? 'rounded-full bg-white p-[7%] shadow-sm'
            : 'dark:rounded-full dark:bg-white/95 dark:p-[7%]'
        }`}
      >
        <img src={icon} alt={name} className="h-full w-full object-contain" />
      </span>
    );
  }

  const IconComponent = icon;
  const brandColor = resolveBrandColor(icon);

  if (!brandColor) {
    return <IconComponent className={className} />;
  }

  if (isActive) {
    return <IconComponent className={className} style={{ color: brandColor.dark }} />;
  }

  return (
    <IconComponent
      className={`${className} text-[color:var(--brand-color)] dark:text-[color:var(--brand-color-dark)]`}
      style={{
        '--brand-color': brandColor.light,
        '--brand-color-dark': brandColor.dark
      } as React.CSSProperties}
    />
  );
};

export const DynamicCategoryIcon: React.FC<DynamicCategoryIconProps> = ({ 
  category, 
  className = "w-5 h-5",
  isActive = false
}) => {
  const [imgError, setImgError] = useState(false);
  const slug = (category.id || '').toLowerCase();
  
  // 1. Check Car Brands via Local SVGs or React Icons
  const brandIcon = BRAND_ICONS[slug] ?? BRAND_ICON_INDEX[normalizeBrandKey(slug)];
  if (brandIcon) {
    return (
      <BrandIcon
        icon={brandIcon}
        name={category.name}
        className={className}
        isActive={isActive}
      />
    );
  }

  // 2. Check if we have a generic icon mapped
  const GenericIcon = CATEGORY_ICONS[slug] || CATEGORY_ICONS[Object.keys(CATEGORY_ICONS).find(k => slug.includes(k)) || ''];
  if (GenericIcon) {
    const defaultColor = CATEGORY_COLORS[Object.keys(CATEGORY_COLORS).find(k => slug.includes(k)) || ''] || 'text-primary-500';
    return <GenericIcon className={`${className} ${!isActive && defaultColor}`} />;
  }

  // 3. Fallback to existing raw DB value if it's a valid URL or emoji
  const rawIcon = (category.iconUrl || category.icon || category.imageUrl || '').toString().trim();
  // Rejects mojibake placeholders ("?", "؟؟", "❓") without punishing valid URLs
  // that legitimately carry a query string.
  const isInvalidIcon = /^[?؟❓\s]+$/.test(rawIcon);

  if (rawIcon && !isInvalidIcon) {
    const isUrl = rawIcon.startsWith('http') || rawIcon.startsWith('/') || rawIcon.startsWith('data:');
    if (isUrl) {
      return (
        <img 
          src={imgError ? BRAND_LOGO_PUBLIC_PATH : rawIcon} 
          alt={category.name} 
          className={`${className} object-contain ${isActive ? 'brightness-0 invert' : ''}`}
          onError={() => setImgError(true)}
        />
      );
    }
    // Only return span if it's a true emoji, preventing rendering plain text like "غرفة نوم" or "?"
    const isEmoji = /^[\p{Emoji_Presentation}\p{Extended_Pictographic}]+$/u.test(rawIcon);
    if (isEmoji) {
      return <span className={`flex items-center justify-center leading-none ${className}`}>{rawIcon}</span>;
    }
  }

  // 4. Ultimate Fallback (Prevent ? and broken text at all costs)
  return <LayoutGrid className={`${className} opacity-50`} />;
};
